import {
  getTabs as getTabsApi,
  createTab as createTabApi,
  editTab as editTabApi,
  deleteTab as deleteTabApi,
  getAllCards as getAllCardsApi,
  getCard as getCardApi,
  createCard as createCardApi,
  editCard as editCardApi,
  deleteCard as deleteCardApi,
  viewCard as viewCardApi
} from './library.api'

import indexedDbService from '@/services/indexedDbService'

import type {
  GetTabsResponse,
  GetTabsResult,
  GetTabRequest,
  GetTabResult,
  CreateTabRequest,
  CreateTabResponse,
  EditTabRequest,
  EditTabResponse,
  DeleteTabRequest,
  DeleteTabResponse,
  GetAllCardsRequest,
  GetAllCardsResponse,
  GetAllCardsResult,
  GetCardRequest,
  GetCardResult,
  CreateCardRequest,
  CreateCardResponse,
  EditCardRequest,
  EditCardResponse,
  DeleteCardRequest,
  DeleteCardResponse,
  ViewCardRequest,
  ViewCardResponse,
  GetAllCardsCachedResponse
} from '@/pages/infoLibrary/config/library.types'

import {
  LIBRARY_CACHE_MAX_AGE_SECONDS,
  TABS_NAMESPACE,
  CARDS_NAMESPACE
} from '@/pages/infoLibrary/config/library.constants'

import { DateTime } from 'luxon'
import { handleError } from '@/utils/error.utils'

/**
 * Info Library Repository - Caching Proxy Layer
 *
 * Pure caching proxy over library.api.ts. Reads from IndexedDB first,
 * falls back to API when cache misses or is stale. Writes go through
 * the API then invalidate relevant caches.
 *
 * Stale data behavior:
 * - List reads (getTabs, getAllCards) return stale data with isStale flag
 * - Single card reads (getCard) return cached list detail with isStale flag
 */

/* =========================================================
   Utility helpers
========================================================= */

function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true
}

const CARDS_LIST_CACHE_KEY = 'getAllCards'

async function getCachedAllCards() {
  const cachedResponse =
    await indexedDbService.getWithMeta<GetAllCardsCachedResponse>(
      CARDS_LIST_CACHE_KEY,
      { namespace: CARDS_NAMESPACE, decrypt: true }
    )

  const cachedData = cachedResponse?.data ?? null

  const resolvedData = cachedData
    ? cachedData.map(({ createdWhen, updatedWhen, viewedWhen, ...card }) => ({
        ...card,
        createdWhen: DateTime.fromISO(createdWhen),
        updatedWhen:
          updatedWhen != null ? DateTime.fromISO(updatedWhen) : updatedWhen,
        viewedWhen:
          viewedWhen != null ? DateTime.fromISO(viewedWhen) : viewedWhen
      }))
    : null

  return { ...cachedResponse, data: resolvedData }
}

async function setCachedAllCards(data: GetAllCardsResponse): Promise<void> {
  await indexedDbService.set(CARDS_LIST_CACHE_KEY, data, {
    namespace: CARDS_NAMESPACE,
    encrypt: true
  })
}

async function syncCachedListCard(card: GetCardResult['data']): Promise<void> {
  const cached = await getCachedAllCards()

  if (!cached?.data?.length) {
    return
  }

  if (!cached.data.some((cachedCard) => cachedCard.itemId === card.itemId)) {
    return
  }

  const nextCards = cached.data.map((cachedCard) =>
    cachedCard.itemId === card.itemId ? card : cachedCard
  )

  await setCachedAllCards(nextCards)
}

/* =========================================================
   Internal stale helper
========================================================= */

interface CacheMetadata {
  cachedAt?: string
}

function getStaleness(metadata: CacheMetadata | null | undefined): {
  isStale: boolean
  ageSeconds: number
} {
  if (!metadata?.cachedAt) {
    return { isStale: true, ageSeconds: Number.MAX_SAFE_INTEGER }
  }

  const cachedAt = DateTime.fromISO(metadata.cachedAt)
  const ageSeconds = DateTime.now().diff(cachedAt, 'seconds').seconds
  const isStale = ageSeconds > LIBRARY_CACHE_MAX_AGE_SECONDS

  return { isStale, ageSeconds }
}

/* =========================================================
   Tabs Repository
========================================================= */

export async function getTabs(): Promise<GetTabsResult> {
  const key = 'getTabs'

  try {
    const cached = await indexedDbService.getWithMeta<GetTabsResponse>(key, {
      namespace: TABS_NAMESPACE,
      decrypt: true
    })

    if (cached?.data?.length) {
      const { data, metadata } = cached
      const { isStale } = getStaleness(metadata)

      return { data, isCache: true, isStale }
    }
  } catch (error) {
    handleError({ error, action: 'getTabs.cacheGet' })
  }

  const data = await getTabsApi()

  try {
    await indexedDbService.set(key, data, {
      namespace: TABS_NAMESPACE,
      encrypt: true
    })
  } catch (error) {
    handleError({ error, action: 'getTabs.cacheSet' })
  }

  return { data, isCache: false, isStale: false }
}

export async function getTab(payload: GetTabRequest): Promise<GetTabResult> {
  const key = `getTabs`

  try {
    const cached = await indexedDbService.getWithMeta<GetTabsResponse>(key, {
      namespace: TABS_NAMESPACE,
      decrypt: true
    })

    const tab = cached?.data?.find((t) => t.itemId === payload.tabId)

    if (tab && cached?.data) {
      const { metadata } = cached
      const { isStale } = getStaleness(metadata)

      return { data: tab, isCache: true, isStale }
    }
  } catch (error) {
    handleError({ error, action: 'getTab.cacheGet' })
  }

  const data = await getTabsApi()

  try {
    await indexedDbService.set(key, data, {
      namespace: TABS_NAMESPACE,
      encrypt: true
    })
  } catch (error) {
    handleError({ error, action: 'getTab.cacheSet' })
  }

  const tab = data.find((t) => t.itemId === payload.tabId)

  if (!tab) return null

  return { data: tab, isCache: false, isStale: false }
}

/* =========================================================
   Cards Repository
========================================================= */

export async function getAllCards(
  payload: GetAllCardsRequest = {},
  options?: { forceRefresh?: boolean }
): Promise<GetAllCardsResult> {
  const unfiltered =
    !payload.tabId &&
    !payload.searchTerm &&
    !payload.filterBy &&
    !payload.sortBy

  const forceRefresh = options?.forceRefresh ?? false

  // Only consult cache for unfiltered, non-force requests
  if (unfiltered && !forceRefresh) {
    try {
      const cached = await getCachedAllCards()

      if (cached?.data?.length) {
        const { isStale } = getStaleness(cached.metadata)

        return { data: cached.data, isCache: true, isStale }
      }
    } catch (err: unknown) {
      console.warn('[LR] Cards cache read failed:', err)
    }
  }

  // Offline + no cache - fail
  if (!isOnline()) throw new Error('library_offline_no_cache')

  // Fetch fresh
  const data = await getAllCardsApi(payload)

  // Only write cache for unfiltered requests
  if (unfiltered) {
    try {
      await setCachedAllCards(data)
    } catch (err: unknown) {
      console.warn('[LR] Cards cache write failed:', err)
    }
  }

  return { data, isCache: false, isStale: false }
}

/* =========================================================
   Single Card Repository
========================================================= */

export async function getCard(
  payload: GetCardRequest,
  options?: { forceRefresh?: boolean }
): Promise<GetCardResult> {
  const forceRefresh = options?.forceRefresh ?? false

  if (!forceRefresh) {
    try {
      const cached = await getCachedAllCards()
      const card =
        cached?.data?.find(
          (cachedCard) => cachedCard.itemId === payload.cardId
        ) ?? null

      if (card) {
        const { isStale } = getStaleness(cached.metadata)

        return { data: card, isCache: true, isStale }
      }
    } catch (error) {
      handleError({ error, action: 'getCard.cacheGet' })
    }
  }

  if (!isOnline()) throw new Error('library_offline_no_cache')

  const data = await getCardApi(payload)

  try {
    await syncCachedListCard(data)
  } catch (error) {
    handleError({ error, action: 'getCard.cacheSet' })
  }

  return { data, isCache: false, isStale: false }
}

/* =========================================================
   CRUD - Tab Operations
========================================================= */

export async function createTab(
  payload: CreateTabRequest
): Promise<CreateTabResponse> {
  const result = await createTabApi(payload)
  await Promise.all([invalidateTabsCache(), invalidateCardsCache()])
  return result
}

export async function editTab(
  payload: EditTabRequest
): Promise<EditTabResponse> {
  const result = await editTabApi(payload)
  await Promise.all([invalidateTabsCache(), invalidateCardsCache()])
  return result
}

export async function deleteTab(
  payload: DeleteTabRequest
): Promise<DeleteTabResponse> {
  const result = await deleteTabApi(payload)
  await Promise.all([invalidateTabsCache(), invalidateCardsCache()])
  return result
}

/* =========================================================
   CRUD - Card Operations
========================================================= */

export async function createCard(
  payload: CreateCardRequest
): Promise<CreateCardResponse> {
  const result = await createCardApi(payload)
  await Promise.all([invalidateTabsCache(), invalidateCardsCache()])
  return result
}

export async function editCard(
  payload: EditCardRequest
): Promise<EditCardResponse> {
  const result = await editCardApi(payload)
  await Promise.all([invalidateTabsCache(), invalidateCardsCache()])
  return result
}

export async function deleteCard(
  payload: DeleteCardRequest
): Promise<DeleteCardResponse> {
  const result = await deleteCardApi(payload)
  await Promise.all([invalidateTabsCache(), invalidateCardsCache()])
  return result
}

export async function viewCard(
  payload: ViewCardRequest
): Promise<ViewCardResponse> {
  const card = await viewCardApi(payload)

  card.viewedWhen =
    card.updatedWhen?.plus({ milliseconds: 1 }) ??
    DateTime.now().plus({ milliseconds: 1 })

  try {
    await syncCachedListCard(card)
  } catch (err: unknown) {
    console.warn('[LR] Failed to sync card cache:', err)
  }
  return card
}

/* =========================================================
   Cache Invalidation
========================================================= */

export async function invalidateTabsCache(): Promise<void> {
  try {
    await indexedDbService.clearNamespace(TABS_NAMESPACE)
  } catch (err: unknown) {
    console.warn('[LR] Failed to invalidate tabs cache:', err)
  }
}

export async function invalidateCardsCache(): Promise<void> {
  try {
    await indexedDbService.clearNamespace(CARDS_NAMESPACE)
  } catch (err: unknown) {
    console.warn('[LR] Failed to invalidate cards cache:', err)
  }
}

export async function invalidateAllCache(): Promise<void> {
  await Promise.all([invalidateTabsCache(), invalidateCardsCache()])
}
